import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function QuickUsageWidget() {
  const { profile } = useAuth();
  const [animated, setAnimated] = useState(false);

  const monthlyLimit = profile?.monthly_message_limit || 5000;
  const sentThisMonth = profile?.messages_sent_this_month || 3420;
  const usagePercent = monthlyLimit > 0 ? Math.min(100, Math.round((sentThisMonth / monthlyLimit) * 100)) : 0;
  const remaining = Math.max(0, monthlyLimit - sentThisMonth);

  const alertLevel = useMemo<'normal' | 'warning' | 'critical' | 'exceeded'>(() => {
    if (sentThisMonth >= monthlyLimit) return 'exceeded';
    if (usagePercent >= 90) return 'critical';
    if (usagePercent >= 70) return 'warning';
    return 'normal';
  }, [usagePercent, sentThisMonth, monthlyLimit]);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const formatNumber = (n: number) => n.toLocaleString('pt-BR');

  const alertConfig = {
    normal: {
      dotColor: 'bg-emerald-500',
      textColor: 'text-emerald-700 dark:text-emerald-400',
      label: 'Dentro do limite',
      barColor: 'bg-emerald-500',
      bg: 'bg-emerald-50 dark:bg-emerald-500/5',
      border: 'border-emerald-200 dark:border-emerald-500/10',
    },
    warning: {
      dotColor: 'bg-amber-500',
      textColor: 'text-amber-700 dark:text-amber-400',
      label: 'Atenção: 70%+ usado',
      barColor: 'bg-amber-500',
      bg: 'bg-amber-50 dark:bg-amber-500/5',
      border: 'border-amber-200 dark:border-amber-500/10',
    },
    critical: {
      dotColor: 'bg-orange-500',
      textColor: 'text-orange-700 dark:text-orange-400',
      label: 'Crítico: 90%+ usado',
      barColor: 'bg-orange-500',
      bg: 'bg-orange-50 dark:bg-orange-500/5',
      border: 'border-orange-200 dark:border-orange-500/10',
    },
    exceeded: {
      dotColor: 'bg-red-500',
      textColor: 'text-red-700 dark:text-red-400',
      label: 'Limite atingido!',
      barColor: 'bg-red-500',
      bg: 'bg-red-50 dark:bg-red-500/5',
      border: 'border-red-200 dark:border-red-500/10',
    },
  }[alertLevel];

  return (
    <div className={`bg-white dark:bg-background-100 rounded-xl border border-background-200 dark:border-background-300 overflow-hidden transition-all duration-500 ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-background-100 dark:border-background-200">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center">
            <i className="ri-speed-up-line text-primary-600 dark:text-primary-400 text-base" />
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground-900 dark:text-foreground-100">
              Uso do Plano
            </h3>
            <p className="text-[10px] text-foreground-400 capitalize">{profile?.plan || 'Starter'}</p>
          </div>
        </div>
        <Link
          to="/my-usage"
          className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
        >
          Meu Uso
          <i className="ri-arrow-right-line text-[10px]" />
        </Link>
      </div>

      {/* Body */}
      <div className="p-5">
        {/* Alert banner for warning+ */}
        {alertLevel !== 'normal' && (
          <div className={`${alertConfig.bg} border ${alertConfig.border} rounded-xl p-3 flex items-start gap-2.5 mb-4`}>
            <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${alertConfig.dotColor}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-semibold ${alertConfig.textColor}`}>{alertConfig.label}</p>
              <p className="text-[10px] text-foreground-500 mt-0.5 leading-relaxed">
                {alertLevel === 'exceeded'
                  ? 'Envios bloqueados. Compre mensagens extras para continuar.'
                  : 'Considere comprar mensagens extras ou fazer upgrade de plano.'}
              </p>
              <Link
                to="/subscription"
                className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-add-circle-line" />
                Comprar excedente
              </Link>
            </div>
          </div>
        )}

        {/* Normal status */}
        {alertLevel === 'normal' && (
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
            <span className="text-xs text-foreground-600 dark:text-foreground-400">
              {formatNumber(remaining)} mensagens restantes este mês
            </span>
          </div>
        )}

        {/* Usage numbers */}
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 tabular-nums">
              {usagePercent}%
            </p>
            <p className="text-[10px] text-foreground-400">utilizado</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-foreground-700 dark:text-foreground-300 tabular-nums">
              {formatNumber(sentThisMonth)}
              <span className="text-foreground-400 font-normal"> / {formatNumber(monthlyLimit)}</span>
            </p>
            <p className="text-[10px] text-foreground-400">mensagens este mês</p>
          </div>
        </div>

        {/* Mini progress bar */}
        <div className="relative mb-1">
          <div className="w-full h-2.5 bg-background-100 dark:bg-background-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ease-out ${alertConfig.barColor}`}
              style={{ width: animated ? `${usagePercent}%` : '0%' }}
            />
          </div>
          {/* Tick at 50%, 75%, 90% */}
          {[50, 75, 90].map((tick) => (
            <div
              key={tick}
              className="absolute top-0 bottom-0 w-px bg-white/50 dark:bg-background-100/50"
              style={{ left: `${tick}%` }}
            />
          ))}
        </div>

        {/* Labels */}
        <div className="flex justify-between text-[9px] text-foreground-400">
          <span>0</span>
          <span>{formatNumber(Math.round(monthlyLimit * 0.5))}</span>
          <span>{formatNumber(monthlyLimit)}</span>
        </div>
      </div>

      {/* Footer quick action */}
      <div className="px-5 py-3 bg-background-50 dark:bg-background-200 border-t border-background-100 dark:border-background-200">
        <div className="flex items-center gap-3">
          <Link
            to="/my-usage"
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 text-xs font-medium text-foreground-600 dark:text-foreground-400 hover:text-foreground-900 dark:hover:text-foreground-200 hover:border-background-300 dark:hover:border-background-400 transition-all duration-200 whitespace-nowrap"
          >
            <i className="ri-bar-chart-line text-sm" />
            Ver detalhes
          </Link>
          <Link
            to="/subscription"
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium transition-all duration-200 whitespace-nowrap active:scale-[0.98]"
          >
            <i className="ri-add-circle-line text-sm" />
            Comprar mais
          </Link>
        </div>
      </div>
    </div>
  );
}